import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Instagram, Youtube, Facebook } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import FooterLogo from './FooterLogo';

const Footer = () => {
  const {
    resolvedTheme
  } = useTheme();
  const [email, setEmail] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const location = useLocation();
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    try {
      const {
        error
      } = await supabase.from('newsletter_subscriptions').insert([{
        email: email.toLowerCase().trim(),
        user_agent: navigator.userAgent
      }]);
      if (error) {
        if (error.code === '23505') {
          // Unique constraint violation
          toast.error('This email is already subscribed to our newsletter.');
        } else {
          throw error;
        }
      } else {
        toast.success('Thank you for subscribing to our newsletter!');
        setEmail('');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast.error('Failed to subscribe. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleLinkClick = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <footer className={cn("mt-20 py-16 px-6 md:px-12", resolvedTheme === 'dark' ? 'bg-netflix-black border-t border-white/10' : 'bg-background border-t')}>
      <div className="max-w-screen-xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-10">
          {/* About Us Section */}
          <div>
            <h3 className="text-sm font-medium mb-4">ABOUT US</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/about" onClick={handleLinkClick} className="text-sm text-muted-foreground hover:text-foreground">
                  Our Story
                </Link>
              </li>
              <li>
                <Link to="/privacy" onClick={handleLinkClick} className="text-sm text-muted-foreground hover:text-foreground">
                  Privacy Policy
                </Link>
              </li>
              <li>
                
              </li>
            </ul>
          </div>

          {/* Contact Section */}
          <div>
            <h3 className="text-sm font-medium mb-4">CONTACT</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/contact" onClick={handleLinkClick} className="text-sm text-muted-foreground hover:text-foreground">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/contact" onClick={handleLinkClick} className="text-sm text-muted-foreground hover:text-foreground">
                  Advertise
                </Link>
              </li>
              <li>
                <Link to="/contact" onClick={handleLinkClick} className="text-sm text-muted-foreground hover:text-foreground">
                  General Inquiries
                </Link>
              </li>
            </ul>
          </div>

          {/* Follow Us Section */}
          <div className="col-span-2 md:col-span-1">
            <h3 className="text-sm font-medium mb-4">FOLLOW US</h3>
            <div className="flex space-x-4">
              <a href="http://instagram.com/everhouse.co" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                <Instagram size={20} />
              </a>
              <a href="http://youtube.com/everhouseco" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                <Youtube size={20} />
              </a>
              <a href="http://facebook.com/everhouse.co" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                <Facebook size={20} />
              </a>
              <a href="https://open.spotify.com/user/31hhpjftvvqrx4ocrhwy6uv22fba?si=98289ae080fd4360" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.84-.179-.959-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.361 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                </svg>
              </a>
              <a href="https://www.tiktok.com/@everhouse.co" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Newsletter Section - Full width on mobile */}
        <div className="mt-10 pt-10 border-t border-border">
          <div className="max-w-md mx-auto md:mx-0">
            <h3 className="text-sm font-medium mb-4 md:text-left text-left">SUBSCRIBE TO EVERHOUSE</h3>
            
            <form onSubmit={handleSubscribe} className="space-y-2">
              <Input type="email" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} className="bg-background" required disabled={isSubmitting} />
              <Button type="submit" disabled={isSubmitting || !email.trim()} className="w-full disabled:opacity-50 text-white bg-netflix-black hover:bg-netflix-gray">
                {isSubmitting ? 'SUBSCRIBING...' : 'SUBSCRIBE'}
              </Button>
            </form>
          </div>
        </div>

        {/* Copyright Section */}
        <div className="mt-16 pt-8 border-t border-border py-0">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <FooterLogo className="mb-4 md:mb-0" />
            <div className="flex flex-col md:flex-row items-center gap-4">
              
              <p className="text-sm text-muted-foreground text-center">
                Copyright © 2009–{new Date().getFullYear()}, Everhouse LLC. All Rights Reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>;
};
export default Footer;